import React, { useState } from 'react';
import { getDiscountCode } from '../util/apiUtils';
import {PITLDiscountDisclaimer, PITLRefundPolicyModal} from '../phsycian-in-the-loop/PITLRefundPolicy';
import HeleneMannaDonationMessage from '../payment/HeleneMannaDonationMessage';
import { formatCurrency, isUKOrder } from '../util/gearOrderUtil';

/**
 * Displays a Discount Code input field and handles code lookup and validation.
 * 
 * @param props.applyDiscount the function to call, passing the discount code, when a code is validated
 * @param props.gearOrder
 * @param props.totalOrderAmountDue the raw numeric value of the order's total amount due after discounts and fees have been applied
 */
export default function DiscountCode({ applyDiscount, gearOrder, totalOrderAmountDue }) {
	const [discountCodeValue, setDiscountCodeValue] = useState('');
	const [isValid, setIsValid] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [showPitlRefundPolicyModal, setShowPitlRefundPolicyModal] = useState(false);

	const codeDisplayName = gearOrder?.physicianInTheLoop ? 'Discount Code' : 'QA Processing Code';

	const updateDiscountCode = (event) => {
		setDiscountCodeValue(event.target.value);
		setIsValid(null);
		setMessage('');
	};

	/**
	 * Fetch and validate the discount details and display a success or failure message.
	 */
	const checkDiscountCode = (event) => {
		event?.preventDefault && event.preventDefault();
		if (discountCodeValue) {
			setIsLoading(true);

			getDiscountCode(gearOrder.id, discountCodeValue)
				.then((json) => {
					const validation = validateDiscount(gearOrder, json);
					setIsValid(validation.valid);
					setMessage(validation.message);
					setIsLoading(false);
					if (validation.valid) {
						applyDiscount(json?.discount);
						setDiscountCodeValue('');
					}
				})
				.catch((e) => {
					setMessage(`Failed to verify ${codeDisplayName}`);
					setIsValid(false);
					setIsLoading(false);
				});
		}
	};

	/**
	 * Check if the discount is valid and return a message to display.
	 * The discount must be for an orderable item that is present on the order (unless it is an order-level PITL discount).
	 * 
	 * @param gearOrder the current order
	 * @param discountResponse the discount response details
	 */
	const validateDiscount = (gearOrder, discountResponse) => {
		let valid = true;
		const discount = discountResponse?.discount;
		let message;
		if (discountResponse?.error) {
			valid = false;
			const errorType = discountResponse.error.error;
			if (errorType === 'DiscountInvalidException' || errorType === 'DiscountNotFoundException') {
				message = discountResponse.error.message || `Enter a valid ${codeDisplayName}`;
			} else {
				message = `Unable to apply discount ${discountResponse.discountCode || ''}`;
			}
		} else if (discount?.discountType === 'CLIENT') {
			message = 'Discount has been applied'
		} else if (discount?.discountType === 'AFFILIATE') {
			if (discount.maxDiscount && (totalOrderAmountDue > discount.maxDiscount)) {
				valid = false;
				message = (
					<span>
						The total cost of your order exceeds the allowed amount.<br />
						Please adjust your order or use an alternative payment method.
					</span>
				);
			} else {
				message = <span>Code <strong>{discount.discountCode || ''}</strong> has been applied</span>
			}
		} else if (discount.discountType === 'PRICE') {
			message = `Discount  has been applied`
		} else if (!discount?.discountAmount) {
			valid = false;
			message = `Enter a valid ${codeDisplayName}`;
		} else {
			let displayValue;
			if (discount.discountType === 'PERCENTAGE') {
				displayValue = formatCurrency(discount.discountAmount, isUKOrder(gearOrder)) + '%'
			} else {
				displayValue = formatCurrency(discount?.discountAmount, isUKOrder(gearOrder));
			}
			message = `A ${displayValue} discount has been applied`;

			if (gearOrder.physicianInTheLoop && discount.discountType === 'PERCENTAGE') {
				message = <PITLDiscountDisclaimer onClick={() => setShowPitlRefundPolicyModal(true)} />;
			}
		}

		return {
			valid: valid,
			message: message
		}
	}

	let formClass = '';
	if (isValid) {
		formClass = 'has-success';
	} else if (isValid === false) {
		formClass = 'has-error';
	}

	// Without an applyDiscount function there is nothing for this component to do
	if (!applyDiscount) {
		return null;
	}

	return (
		<div>
			<form className={`form-inline ${formClass}`} style={{ margin: '0', display: 'inline-block' }}>
				<div className="form-group">
					<input
						type="text"
						className="form-control"
						style={{ borderRadius: '5px' }}
						placeholder={codeDisplayName}
						value={discountCodeValue}
						onChange={updateDiscountCode}
						readOnly={isLoading}
					/>
				
				{isLoading ? (
					<span style={{ fontSize: '30px', verticalAlign: 'middle', marginLeft: '10px', lineHeight: '1' }}>
						<i className="fa fa-spinner fa-spin fa-fw" style={{minWidth: '70px'}}></i>
					</span>
				) : (
					<button
						className="btn btn-primary btn-sm margin-clear ms-2"
						style={{ borderRadius: '5px', minWidth: '70px' }}
						onClick={checkDiscountCode}
						disabled={isLoading || !discountCodeValue}
					>
						Apply
					</button>
				)}
				{message && <span className="help-block">{message}</span>}
				</div>
			</form>
			{gearOrder?.physicianInTheLoop && <HeleneMannaDonationMessage />}
			{showPitlRefundPolicyModal && (
				<PITLRefundPolicyModal onClose={()=>setShowPitlRefundPolicyModal(false)} />
			)}
		</div>
	);
}
